import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IAdminUser from '../../interfaces/IAdminUser'
import {
  SearchRequest,
  AdminUserCreateRequest,
  AdminUserUpdateRequest,
  AdminUserDeleteRequest,
  AdminUserResetPasswordRequest
} from '../../services/api/interfaces/ApiRequest'
import {
  adminUserList,
  adminUserCreate,
  adminUserUpdate,
  adminUserDelete,
  adminUserResetPassword
} from '../../services/api'

interface AdminUserContextState extends BaseContextState {
  users: Array<IAdminUser> | null,
  user: IAdminUser | null
}

interface AdminUserContextData extends AdminUserContextState {
  resetErrors: Function,
  requestAdminUserList: (args: SearchRequest) => void,
  requestAdminUserFind: (args: SearchRequest) => void,
  requestAdminUserCreate: (args: AdminUserCreateRequest) => Promise<any>,
  requestAdminUserUpdate: (args: AdminUserUpdateRequest) => Promise<any>,
  requestAdminUserDelete: (args: AdminUserDeleteRequest) => Promise<any>,
  requestAdminUserResetPassword: (args: AdminUserResetPasswordRequest) => Promise<any>,
}

// Create context
const { Provider, Consumer } = createContext({} as AdminUserContextData)

// Export consumer
export const AdminUserConsumer = Consumer

export class AdminUserProvider extends BaseContextProvider {
  state: AdminUserContextState = {
    users: null,
    user: null
  }

  lastListPayload: SearchRequest | null = null

  /**
   * Make api request for admin user list
   * 
   * @param {SearchRequest} payload 
   */
  requestAdminUserList = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null,
      user: null
    }, async () => {
      this.lastListPayload = payload
      const response = await adminUserList(payload)

      // Process our response
      this.processResponse(response, { 
        paginate: {
          destination: 'users'
        }
      })
    })
  }

  /**
   * Make api request for admin user finder
   * 
   * @param {SearchRequest} payload 
   */
  requestAdminUserFind = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null,
      user: null
    }, async () => {
      payload.options = {
        ...payload.options,
        _single: true
      }
      const response = await adminUserList(payload)

      // Process our response
      this.processResponse(response, { 
        mappings: {
          destination: 'user'
        }
      })
    })
  }

  /**
   * Make api request for admin user create
   * 
   * @param {AdminUserCreateRequest} payload 
   */
  requestAdminUserCreate = (payload: AdminUserCreateRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        user: null
      }, async () => {
        let response = await adminUserCreate(payload)
        this.processResponse(response, {
          mappings: [
            { origin: 'admin_user', destination: 'user' }
          ]
        })
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for admin user delete
   * 
   * @param {AdminUserUpdateRequest} payload 
   */
  requestAdminUserUpdate = (payload: AdminUserUpdateRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await adminUserUpdate(payload)
        this.processResponse(response, {
          mappings: [
            { origin: 'admin_user', destination: 'user' }
          ]
        })
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for admin user delete
   * 
   * @param {AdminUserDeleteRequest} payload 
   */
  requestAdminUserDelete = (payload: AdminUserDeleteRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await adminUserDelete(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          this.requestAdminUserList(this.lastListPayload || {})
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for admin user reset password
   * 
   * @param {AdminUserResetPasswordRequest} payload 
   */
   requestAdminUserResetPassword = (payload: AdminUserResetPasswordRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await adminUserResetPassword(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          this.requestAdminUserList(this.lastListPayload || {})
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }
  
  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestAdminUserList: this.requestAdminUserList,
          requestAdminUserFind: this.requestAdminUserFind,
          requestAdminUserCreate: this.requestAdminUserCreate,
          requestAdminUserUpdate: this.requestAdminUserUpdate,
          requestAdminUserDelete: this.requestAdminUserDelete,
          requestAdminUserResetPassword: this.requestAdminUserResetPassword
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
